import { useState } from "react";
import { Play, Save, Trash2, Cloud, CloudOff, AlertTriangle, Circle, Lock, Loader2, ShieldCheck, ShieldOff, History, Search, Target, CheckCircle2, Copy, ClipboardPaste } from "lucide-react";
import { formatLocalDate } from "../../../../shared/common/datetimeUtils";

// Delete confirmation modal
function DeleteConfirmModal({ isOpen, scenarioName, onConfirm, onCancel, isDarkMode }) {
  if (!isOpen) return null;

  const overlay = "fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm";
  const modal = isDarkMode
    ? "bg-gray-900 border-gray-700 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div className={overlay} onClick={onCancel}>
      <div
        className={`relative w-full max-w-md rounded-2xl border p-6 shadow-2xl ${modal}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/20">
          <AlertTriangle className="h-7 w-7 text-red-500" />
        </div>

        {/* Title */}
        <h3 className="mb-2 text-center text-lg font-semibold">
          Delete Scenario
        </h3>

        {/* Message */}
        <p className={`mb-6 text-center text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
          Are you sure you want to delete{" "}
          <span className="font-semibold text-red-400">"{scenarioName}"</span>?
          <br />
          This action cannot be undone.
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
              isDarkMode
                ? "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ScenarioToolbar({
  isDarkMode,
  microcycleTemplates,
  activeSlot,
  onRename,
  onSaveScenario,
  onLaunchScenario,
  onDeactivateScenario,
  onDeleteScenario,
  canAddScenario,
  hasUnsavedChanges = false,
  isLaunching = false,
  hasTargetMatch = false,
  onSearchTargetMatch,
  onCopyCalendar,
  onPasteCalendar,
  hasCopiedCalendar = false,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const shell = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  const isRunning = activeSlot?.status === "queued" || activeSlot?.status === "running";
  
  // Check if scenario is past (target date < today) — checked BEFORE isActiveScenario
  const isPastScenario = (() => {
    if (activeSlot?.scenario?.is_past) return true;
    const activeUntil = activeSlot?.scenario?.active_until;
    if (activeUntil) {
      const targetDate = new Date(activeUntil);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      return targetDate < today;
    }
    return false;
  })();

  // Check if scenario is active (simulation in progress until target date)
  // Past scenarios are NEVER considered active
  const isActiveScenario = (() => {
    if (isPastScenario) return false;
    if (activeSlot?.status === "active" || activeSlot?.scenario?.is_active) {
      const activeUntil = activeSlot?.scenario?.active_until;
      if (activeUntil) {
        const targetDate = new Date(activeUntil);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        targetDate.setHours(0, 0, 0, 0);
        return targetDate >= today;
      }
      return true;
    }
    return false;
  })();
  
  const isLocked = isRunning || isActiveScenario || isPastScenario || activeSlot?.scenario?.locked || isLaunching;
  const noTargetMatch = !hasTargetMatch && !isLocked;
  const disabledLaunch = isLocked || noTargetMatch;
  const isSaved = !!activeSlot?.scenario?.microcycle_template_id;

  // Progress tracking
  const runProgress = activeSlot?.runProgress ?? 0;
  const computingDone = activeSlot?.computingDone ?? false;
  const isComputing = isActiveScenario && !computingDone;

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (onDeleteScenario) {
      onDeleteScenario();
    }
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        scenarioName={activeSlot?.scenario?.name || "Scenario"}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isDarkMode={isDarkMode}
      />

      <div className={`rounded-2xl border p-4 ${shell} ${isLocked ? isComputing ? 'ring-2 ring-blue-500/40' : computingDone && isActiveScenario ? 'ring-2 ring-emerald-500/40' : 'ring-2 ring-blue-500/40' : ''}`}>
        {/* Locked / Active banner */}
        {isLocked && (
          <div className={`mb-3 rounded-xl border px-3 py-2 text-sm font-medium ${
            isPastScenario
              ? isDarkMode
                ? 'bg-gray-800 border-gray-600 text-gray-400'
                : 'bg-gray-50 border-gray-200 text-gray-600'
              : isActiveScenario && computingDone
              ? isDarkMode
                ? 'bg-emerald-900/30 border-emerald-700/50 text-emerald-300'
                : 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : isActiveScenario
              ? isDarkMode
                ? 'bg-blue-900/30 border-blue-700/50 text-blue-300'
                : 'bg-blue-50 border-blue-200 text-blue-700'
              : isDarkMode
                ? 'bg-gray-800 border-gray-600 text-gray-300'
                : 'bg-gray-50 border-gray-200 text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              {isLaunching ? (
                <Loader2 size={16} className="animate-spin" />
              ) : isPastScenario ? (
                <History size={16} />
              ) : isActiveScenario && computingDone ? (
                <CheckCircle2 size={16} className={isDarkMode ? 'text-emerald-400' : 'text-emerald-500'} />
              ) : isActiveScenario ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Lock size={16} />
              )}
              <span className="flex-1">
                {isLaunching
                  ? "Launching simulation…"
                  : isPastScenario
                    ? `Past scenario${activeSlot?.scenario?.active_until ? ` (target date: ${formatLocalDate(activeSlot.scenario.active_until, { locale: 'en-US', day: 'numeric', month: 'long', year: 'numeric' })})` : ''}. This scenario is read-only.`
                    : isActiveScenario && computingDone
                    ? `Simulation complete ✓${activeSlot?.scenario?.active_until ? ` — Active until ${formatLocalDate(activeSlot.scenario.active_until, { locale: 'en-US', day: 'numeric', month: 'long', year: 'numeric' })}` : ''}. Results are available in the Results tab.`
                    : isActiveScenario
                    ? `Computing (${Math.round(runProgress * 100)}%)${activeSlot?.scenario?.active_until ? ` — Until ${formatLocalDate(activeSlot.scenario.active_until, { locale: 'en-US', day: 'numeric', month: 'long', year: 'numeric' })}` : ''}…`
                    : "This scenario is locked."
                }
              </span>
              {isActiveScenario && onDeactivateScenario && (
                <button
                  onClick={onDeactivateScenario}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                    isDarkMode
                      ? 'bg-red-900/40 hover:bg-red-900/60 text-red-300 border border-red-700/50'
                      : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                  }`}
                  title="Deactivate this scenario"
                >
                  <ShieldOff size={14} />
                  Deactivate
                </button>
              )}
            </div>

            {/* Progress bar during computing */}
            {isComputing && (
              <div className="mt-2">
                <div className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'}`}>
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-700 ease-out"
                    style={{ width: `${Math.max(2, Math.round(runProgress * 100))}%` }}
                  />
                </div>
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-blue-400/80' : 'text-blue-600/80'}`}>
                  {Math.round(runProgress * 100)}% — Processing player-by-player simulations…
                </p>
              </div>
            )}
          </div>
        )}

        {/* No target match warning */}
        {noTargetMatch && (
          <div className={`mb-3 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium border ${
            isDarkMode
              ? 'bg-amber-900/20 border-amber-700/50 text-amber-300'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'
            }`}>
              <Target size={18} className={isDarkMode ? 'text-amber-400' : 'text-amber-600'} />
            </div>
            <div className="flex-1">
              <p className="font-semibold">No target match detected</p>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-amber-400/80' : 'text-amber-700'}`}>
                A target match is required to launch predictive simulations. Use the button below to search and import a match from the calendar.
              </p>
            </div>
            {onSearchTargetMatch && (
              <button
                onClick={onSearchTargetMatch}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition whitespace-nowrap ${
                  isDarkMode
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-amber-600 hover:bg-amber-700 text-white'
                }`}
              >
                <Search size={14} />
                Search target match
              </button>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <label className={`text-xs ${muted}`}>Scenario name</label>
              {isLocked ? (
                <span className="inline-flex items-center gap-1 text-xs text-blue-400">
                  {isPastScenario ? <History size={12} /> : isActiveScenario ? <ShieldCheck size={12} /> : <Lock size={12} />}
                  {isPastScenario ? "Past scenario" : isActiveScenario ? "Simulation running" : "Locked"}
                </span>
              ) : isSaved ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                  <Cloud size={12} /> Saved
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                  <CloudOff size={12} /> Not saved
                </span>
              )}
            </div>
            <div className="flex mt-1">
              <span className={`inline-flex items-center rounded-l-xl border border-r-0 px-3 py-2 text-sm font-medium select-none ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-gray-400"
                  : "bg-gray-100 border-gray-200 text-gray-500"
              }`}>scenario_</span>
              <input
                value={(activeSlot?.scenario?.name || "").replace(/^scenario_/, "")}
                onChange={(e) => onRename("scenario_" + e.target.value)}
                disabled={isLocked}
                className={`w-full rounded-r-xl border px-3 py-2 text-sm outline-none ${isLocked ? 'opacity-60 cursor-not-allowed ' : ''}${
                  isDarkMode
                    ? "bg-gray-950 border-gray-700 text-gray-100"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
                placeholder="e.g. week_12_rotation"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Search target match button */}
            {onSearchTargetMatch && (
              <button
                onClick={onSearchTargetMatch}
                disabled={hasTargetMatch || isLocked}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                  hasTargetMatch
                    ? isDarkMode
                      ? 'border-emerald-700/50 bg-emerald-900/20 text-emerald-400 cursor-default opacity-80'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-600 cursor-default opacity-80'
                    : isLocked
                    ? 'opacity-50 cursor-not-allowed border-gray-700 bg-gray-950 text-gray-400'
                    : isDarkMode
                    ? 'border-amber-600/50 bg-amber-900/20 hover:bg-amber-900/40 text-amber-300'
                    : 'border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700'
                }`}
                title={hasTargetMatch ? 'Target match already present in calendar' : 'Search for a target match from the calendar'}
              >
                {hasTargetMatch ? (
                  <CheckCircle2 size={16} className={isDarkMode ? 'text-emerald-400' : 'text-emerald-500'} />
                ) : (
                  <Search size={16} />
                )}
                {hasTargetMatch ? 'Target match found' : 'Search target match'}
              </button>
            )}

            <button
              onClick={onSaveScenario}
              disabled={isLocked}
              className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${isLocked ? 'opacity-50 cursor-not-allowed ' : ''}${
                hasUnsavedChanges
                  ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-500"
                  : isDarkMode
                    ? "bg-gray-950 border-gray-700 hover:bg-gray-900 text-gray-200"
                    : "bg-white border-gray-200 hover:bg-gray-50 text-gray-800"
              }`}
            >
              {hasUnsavedChanges && (
                <Circle size={8} className="fill-white text-white animate-pulse" />
              )}
              <Save size={16} />
              {hasUnsavedChanges ? "Save*" : "Save scenario"}
            </button>

            {/* Copy / Paste calendar */}
            {onCopyCalendar && (
              <button
                onClick={onCopyCalendar}
                disabled={!hasTargetMatch}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                  !hasTargetMatch
                    ? 'opacity-40 cursor-not-allowed border-gray-700 bg-gray-950 text-gray-500'
                    : isDarkMode
                      ? "bg-gray-950 border-gray-700 hover:bg-gray-900 text-gray-200"
                      : "bg-white border-gray-200 hover:bg-gray-50 text-gray-800"
                }`}
                title={hasTargetMatch ? "Copy this calendar (sessions relative to match day)" : "A target match is needed to copy"}
              >
                <Copy size={16} />
                Copy
              </button>
            )}
            {onPasteCalendar && (
              <button
                onClick={onPasteCalendar}
                disabled={!hasCopiedCalendar || !hasTargetMatch || isLocked}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                  !hasCopiedCalendar || !hasTargetMatch || isLocked
                    ? 'opacity-40 cursor-not-allowed border-gray-700 bg-gray-950 text-gray-500'
                    : isDarkMode
                      ? "bg-indigo-600/20 border-indigo-500/40 hover:bg-indigo-600/30 text-indigo-300"
                      : "bg-indigo-50 border-indigo-200 hover:bg-indigo-100 text-indigo-700"
                }`}
                title={
                  !hasCopiedCalendar ? "Nothing copied yet"
                    : !hasTargetMatch ? "A target match is needed to paste"
                    : isLocked ? "Scenario is locked"
                    : "Paste copied sessions (aligned to match day)"
                }
              >
                <ClipboardPaste size={16} />
                Paste
              </button>
            )}

            {isSaved && onDeleteScenario && !isLocked && (
              <button
                onClick={handleDeleteClick}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
                  isDarkMode
                    ? "bg-gray-950 border-gray-700 hover:bg-red-900/30 text-red-400"
                    : "bg-white border-gray-200 hover:bg-red-50 text-red-600"
                }`}
                title="Delete from database"
              >
                <Trash2 size={16} />
              </button>
            )}

            <button
              onClick={onLaunchScenario}
              disabled={disabledLaunch}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                disabledLaunch
                  ? isPastScenario
                    ? "bg-gray-500/30 text-gray-400 cursor-not-allowed"
                    : isActiveScenario && computingDone
                    ? "bg-emerald-600/30 text-emerald-300 cursor-not-allowed"
                    : isActiveScenario
                    ? "bg-blue-600/30 text-blue-300 cursor-not-allowed"
                    : "bg-gray-500/30 text-gray-400 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
              title={noTargetMatch ? "No target match detected" : isPastScenario ? "Target date is in the past" : isActiveScenario && computingDone ? "Simulation complete — results available" : isActiveScenario ? `Computing (${Math.round(runProgress * 100)}%)` : isLaunching ? "Launching..." : "Start simulation"}
            >
              {isLaunching ? (
                <Loader2 size={16} className="animate-spin" />
              ) : isPastScenario ? (
                <History size={16} />
              ) : isActiveScenario && computingDone ? (
                <CheckCircle2 size={16} />
              ) : isActiveScenario ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Play size={16} />
              )}
              {isLaunching ? "Launching..." : noTargetMatch ? "Target match required" : isPastScenario ? "Past target date" : isActiveScenario && computingDone ? "Simulation complete ✓" : isActiveScenario ? `Computing ${Math.round(runProgress * 100)}%` : "Start simulation"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
